import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetTasksQuery } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import type { TicLinkedWorkTaskQueryScope } from '~redux/modules/tic/tasks/tic.tasks.scope';

export interface ScopedTaskBrowser {
  browseKey: string;
  browseTasks: Array<PresentableTaskResource>;
  isInitialLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  hasFetched: boolean;
  loadMore: () => void;
  error: unknown;
}

interface UseScopedTaskBrowserParams {
  accountId: string | undefined;
  taskQueryScope: TicLinkedWorkTaskQueryScope;
  pageSize: number;
  skip?: boolean;
}

// Pages the server-scoped task feed for both linked-work pickers.
export const useScopedTaskBrowser = ({
  accountId,
  taskQueryScope,
  pageSize,
  skip = false,
}: UseScopedTaskBrowserParams): ScopedTaskBrowser => {
  const browseKey = `${accountId ?? 'no-account'}|${
    taskQueryScope.projectUri ?? taskQueryScope.toolId ?? 'all'
  }|${taskQueryScope.status ?? 'both'}`;

  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = useState<Array<PresentableTaskResource>>([]);
  const [activeKey, setActiveKey] = useState(browseKey);
  // Retain page depth for request telemetry and compatibility with cursor-silent servers.
  const [depth, setDepth] = useState(1);
  // Allow one restart if a rolling deployment invalidates a cursor.
  const [recoveredBrowseKey, setRecoveredBrowseKey] = useState<string | null>(null);
  const [recoveryNeedsRefetch, setRecoveryNeedsRefetch] = useState(false);
  const [recoveryBlockedFulfilledAt, setRecoveryBlockedFulfilledAt] = useState<number>();
  const firstPageFulfilledAt = useRef<number>();

  // Reset during render so the query cannot run once with a new scope and the old cursor.
  if (browseKey !== activeKey) {
    setActiveKey(browseKey);
    setCursor(undefined);
    setAccumulated([]);
    setDepth(1);
    setRecoveredBrowseKey(null);
    setRecoveryNeedsRefetch(false);
    setRecoveryBlockedFulfilledAt(undefined);
  }
  const effectiveCursor = browseKey === activeKey ? cursor : undefined;

  const query = useGetTasksQuery(
    {
      accountId: accountId ?? '',
      limit: pageSize,
      cursor: effectiveCursor,
      page: browseKey === activeKey ? depth : 1,
      ...taskQueryScope,
    },
    { skip: skip || accountId == null || taskQueryScope.status === 'none' }
  );
  if (
    effectiveCursor == null &&
    recoveredBrowseKey !== browseKey &&
    query.fulfilledTimeStamp != null
  ) {
    firstPageFulfilledAt.current = query.fulfilledTimeStamp;
  }

  // `currentData` becomes undefined while args change; `data` retains the previous successful page
  // and can leave a freshly reset accumulator empty.
  const recoveryPagePending =
    recoveredBrowseKey === browseKey &&
    effectiveCursor == null &&
    (query.isFetching || query.fulfilledTimeStamp === recoveryBlockedFulfilledAt);
  const pageTasks = recoveryPagePending ? undefined : query.currentData?.tasks;
  useEffect(() => {
    if (pageTasks == null) return;
    const tasks = pageTasks.filter((task): task is PresentableTaskResource => task.type === 'task');
    if (tasks.length === 0) return;
    setAccumulated(prev => {
      const seen = new Set(prev.map(task => task.uri));
      const additions: Array<PresentableTaskResource> = [];
      for (const task of tasks) {
        if (seen.has(task.uri)) continue;
        seen.add(task.uri);
        additions.push(task);
      }
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, [pageTasks]);

  // An absent next_cursor means an older server; null means the cursor feed is exhausted.
  const meta = query.data?.meta;
  const serverPagesByCursor = meta != null && 'next_cursor' in meta;
  const nextCursor = serverPagesByCursor ? (meta.next_cursor ?? null) : null;
  const lastPageCount = query.data?.tasks?.length ?? 0;

  const errorStatus =
    typeof query.error === 'object' && query.error != null && 'status' in query.error
      ? query.error.status
      : undefined;
  useEffect(() => {
    if (errorStatus !== 400 || effectiveCursor == null || recoveredBrowseKey === browseKey) return;
    setRecoveredBrowseKey(browseKey);
    setCursor(undefined);
    setAccumulated([]);
    setDepth(1);
    setRecoveryBlockedFulfilledAt(firstPageFulfilledAt.current);
    setRecoveryNeedsRefetch(true);
  }, [browseKey, effectiveCursor, errorStatus, recoveredBrowseKey]);
  useEffect(() => {
    if (!recoveryNeedsRefetch || effectiveCursor != null) return;
    setRecoveryNeedsRefetch(false);
    query.refetch();
  }, [effectiveCursor, query, recoveryNeedsRefetch]);

  const hasMore =
    !query.isFetching &&
    query.error == null &&
    query.data != null &&
    (serverPagesByCursor ? nextCursor != null : lastPageCount >= pageSize);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    setCursor(nextCursor ?? undefined);
    setDepth(d => d + 1);
  }, [hasMore, nextCursor]);

  const isInitialLoading =
    accumulated.length === 0 && (query.isLoading || query.isFetching) && !skip;
  const isFetchingMore = accumulated.length > 0 && query.isFetching;

  const hasFetched = query.data != null || query.error != null;

  return useMemo(
    () => ({
      browseKey,
      browseTasks: accumulated,
      isInitialLoading,
      isFetchingMore,
      hasMore,
      hasFetched,
      loadMore,
      error: query.error,
    }),
    [
      accumulated,
      browseKey,
      isInitialLoading,
      isFetchingMore,
      hasMore,
      hasFetched,
      loadMore,
      query.error,
    ]
  );
};
